import React from "react";
import Basicbar from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import { getCurrencySymbol } from "../../../../../Utils/services";
import styles from "../scss/AppointmentEngagementChart.module.scss";

const CHART_CONTAINER_STYLES = {
  width: "100%",
  minWidth: 250,
  maxWidth: 760,
  margin: "0 auto",
  position: "relative",
};

// Simple bar chart for Appointment Engagement
export default function AppointmentEngagementChart({
  items = [],
  columns = [],
} = {}) {
  // Data processing moved here from widget
  // Sort by day_index if present to keep Monday..Sunday order
  const sortedItems = React.useMemo(() => {
    const arr = Array.isArray(items) ? items.slice() : [];
    arr.sort((a, b) => {
      const ai = typeof a.day_index === "number" ? a.day_index : 0;
      const bi = typeof b.day_index === "number" ? b.day_index : 0;
      return ai - bi;
    });
    return arr;
  }, [items]);

  const categories = React.useMemo(
    () => (sortedItems || []).map((r) => r.day_name || ""),
    [sortedItems],
  );

  function parseCurrency(val) {
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      // remove currency symbols and commas
      const num = Number(val.replace(/[^0-9.-]+/g, ""));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }

  // Build up to 4 series based on provided columns example
  const series = React.useMemo(() => {
    const dataRows = sortedItems || [];
    const defs = [
      {
        key: "total_appointments",
        name: "Total Appointments",
        type: "count",
        color: { from: "#4facfe", to: "#047ec3" },
      },
      {
        key: "checked_in_appointments",
        name: "Checked-In Appointments",
        type: "count",
        color: { from: "#43e97b", to: "#00a896" },
      },
      {
        key: "avg_quick_pay_sales",
        name: "Quick Pay Sales",
        type: "currency",
        color: { from: "#f6d365", to: "#ff8c42" },
      },
      {
        key: "avg_procedure_sales",
        name: "Procedure Sales",
        type: "currency",
        color: { from: "#a18cd1", to: "#9b59b6" },
      },
    ];

    const present = defs.filter((d) => columns.indexOf(d.key) !== -1);
    const gradient = (from, to) => ({
      linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
      stops: [
        [0, from],
        [1, to],
      ],
    });

    return present.map((d) => {
      const rawData = dataRows.map((r) => {
        const v = r[d.key];
        return d.type === "currency"
          ? parseCurrency(v)
          : typeof v === "number"
          ? v
          : Number(v) || 0;
      });
      return {
        name: d.name,
        data: rawData,
        color:
          typeof d.color === "object"
            ? gradient(d.color.from, d.color.to)
            : d.color,
        yAxis: d.type === "currency" ? 1 : 0,
        borderRadius: 6,
        pointPadding: 0.08,
      };
    });
  }, [sortedItems, columns]);

  // Determine if there is any non-zero value across all series
  const hasData = React.useMemo(() => {
    return series.some(
      (s) =>
        Array.isArray(s.data) &&
        s.data.some((v) => v !== 0 && v !== null && typeof v !== "undefined"),
    );
  }, [series]);

  const countsFormat = "{point.y}";
  const currency =
    getCurrencySymbol && typeof getCurrencySymbol === "function"
      ? getCurrencySymbol()
      : "$";
  if (!series.length || !categories.length || !hasData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "330px",
          padding: "1rem",
          textAlign: "center",
          fontSize: 14,
          opacity: 0.7,
        }}
      >
        No appointment engagement data
      </div>
    );
  }

  const height = 800;
  const BAR_POINT_WIDTH = 8;

  return (
    <div
      className="highcharts-container"
      data-highcharts-chart="0"
      style={{ ...CHART_CONTAINER_STYLES, height: height + 80 }}
    >
      <div className="dashboardSectionContent">
        <Basicbar
          highcharts={Highcharts}
          containerProps={{
            className: styles.chartContainer,
          }}
          options={{
            chart: {
              type: "bar",
              height: height,
              spacingTop: 50,
              spacingRight: 20,
              spacingBottom: 18,
              spacingLeft: 10,
              style: { fontFamily: "inherit" },
            },
            title: { text: "" },
            xAxis: {
              categories,
              title: { text: null },
              tickLength: 0,
              lineWidth: 0,
              labels: {
                style: { fontSize: "13px", fontWeight: 500 },
                formatter: function () {
                  return this.value;
                },
              },
            },
            yAxis: [
              {
                // hidden numeric axis for counts/currency
                visible: false,
              },
              { visible: false },
            ],
            legend: {
              align: "center",
              verticalAlign: "top",
              layout: "horizontal",
              itemStyle: { fontWeight: "normal", fontSize: "12px" },
              symbolRadius: 4,
              y: 0,
              padding: 4,
            },
            tooltip: {
              shared: true,
              borderRadius: 6,
              backgroundColor: "rgba(255,255,255,0.95)",
              shadow: true,
              style: { color: "#222", fontSize: "12px" },
              formatter: function () {
                const points = this.points || [];
                let s = `<div style="margin-bottom:4px"><b>${this.x}</b></div>`;
                points.forEach((pt) => {
                  const usesCurrency =
                    pt.series.yAxis &&
                    pt.series.yAxis.userOptions &&
                    pt.series.yAxis.userOptions.index === 1;
                  const val = pt.y;
                  const label = usesCurrency
                    ? `${currency}${val.toFixed(2)}`
                    : val;
                  s += `<div style="display:flex;align-items:center;gap:6px;line-height:1.2"><span style="width:10px;height:10px;border-radius:2px;display:inline-block;background:${pt.color}"></span><span style="flex:1">${pt.series.name}</span><b>${label}</b></div>`;
                });
                return s;
              },
              useHTML: true,
            },
            plotOptions: {
              series: {
                minPointLength: 3,
                animation: { duration: 400 },
                pointPadding: 0.08,
                groupPadding: 0.18,
                pointWidth: BAR_POINT_WIDTH,
                borderWidth: 1,
                borderColor: "rgba(0,0,0,0.06)",
                dataLabels: {
                  enabled: true,
                  inside: false,
                  align: "left",
                  formatter: function () {
                    const usesCurrency =
                      this.series.yAxis &&
                      this.series.yAxis.userOptions &&
                      this.series.yAxis.userOptions.index === 1;
                    if (usesCurrency) {
                      return `${currency}${Number(this.y).toFixed(2)}`;
                    }
                    return countsFormat.replace("{point.y}", this.y);
                  },
                  style: {
                    fontSize: "12px",
                    fontWeight: 500,
                    textOutline: "none",
                  },
                  x: 6,
                },
                states: {
                  inactive: { opacity: 0.35 },
                  hover: { brightness: 0.08 },
                },
                point: {
                  events: {
                    mouseOver: function () {
                      this.graphic && this.graphic.attr({ zIndex: 10 });
                    },
                  },
                },
              },
            },
            series,
            credits: { enabled: false },
            responsive: {
              rules: [
                {
                  condition: { maxWidth: 540 },
                  chartOptions: {
                    chart: { height: Math.max(220, categories.length * 56) },
                    xAxis: { labels: { style: { fontSize: "12px" } } },
                    legend: { itemStyle: { fontSize: "11px" } },
                    plotOptions: {
                      series: { dataLabels: { style: { fontSize: "11px" } } },
                    },
                  },
                },
              ],
            },
          }}
        />
      </div>
    </div>
  );
}
